/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Grid, Link, Typography2 } from '@coursera/cds-core';
import type { AssignmentCalendarItem as Assignment } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';
import { AssignmentCalendarItem_SubmissionType as AssignmentSubmissionType } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';

import { getItemTypePrefixTranslationByTypeName } from 'bundles/author-course/utils/ItemTypeUtils';
import ItemIcon from 'bundles/item/components/ItemIcon';
import type { ItemType } from 'bundles/item/types/ItemType';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { truncToPlace } from 'bundles/primitive-formatting/utils/numberUtils';

import _t from 'i18n!nls/degree-home';

import Pill from '../Pill';

const styles: Record<string, Interpolation<Theme>> = {
  itemSubText: () => css`
    color: var(--cds-color-grey-600);
    font-size: 14px;
  `,
  itemName: css`
    font-weight: normal;
  `,
  itemHeaderLine: css`
    display: inline;
  `,
  itemHeaderText: css`
    margin-right: 16px;
  `,
  itemPillContainer: css`
    display: inline-block;
  `,
  dotSeparator: () => css`
    margin: 0 var(--cds-spacing-100);
  `,
  gradeWeight: css`
    display: inline-block;
  `,
  courseName: css`
    a {
      color: inherit !important;
    }
  `,
  itemIcon: css`
    padding-left: 0 !important;
    padding-right: 0 !important;
  `,
};

const dotSeparator = (
  <span aria-hidden="true" css={styles.dotSeparator}>
    •
  </span>
);

const RecentGradesListItem = ({
  itemIcon,
  itemTypeName,
  itemName,
  itemUrl,
  statusPill,
  subHeading,
  trackingName,
}: {
  itemIcon: React.ReactElement;
  itemTypeName: string;
  itemName: string;
  itemUrl: string;
  statusPill?: React.ReactElement | null;
  subHeading: React.ReactChild;
  trackingName: string;
}) => {
  const headerContents = (
    <>
      {itemTypeName}: <span css={styles.itemName}>{itemName}</span>
    </>
  );

  return (
    <Grid container item spacing={12}>
      <Grid container item xs={1} justifyContent="center" css={styles.itemIcon} aria-hidden="true">
        {itemIcon}
      </Grid>
      <Grid container item xs={11} spacing={8}>
        <Grid container item xs={12} css={styles.itemHeaderLine}>
          {itemUrl ? (
            <Link
              component={TrackedA}
              trackingName={trackingName}
              href={itemUrl}
              variant="quiet"
              typographyVariant="subtitleMedium"
              css={styles.itemHeaderText}
              data-e2e={`${itemTypeName} link ${itemName}`}
            >
              {headerContents}
            </Link>
          ) : (
            <Typography2 component="h3" variant="subtitleMedium">
              {headerContents}
            </Typography2>
          )}

          {statusPill && <span css={styles.itemPillContainer}>{statusPill}</span>}
        </Grid>
        <Grid item>
          <div css={styles.itemSubText}>{subHeading}</div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const RecentGradesItem = ({ item }: { item: Assignment }) => {
  let statusPill;
  if (item.grade !== undefined) {
    statusPill = (
      <Pill
        rootClassName="grade-pill"
        data-e2e="grade-pill"
        label={_t('Grade: #{grade}%', {
          grade: truncToPlace(item.grade * 100, 2),
        })}
        textColor="var(--cds-color-green-700)"
        backgroundColor="var(--cds-color-green-50)"
      />
    );
  }

  const subHeading = (
    <span>
      <span css={styles.courseName}>
        <Link href={item.courseUrl} data-e2e={`recent-grades-item-course-name-link ${item.courseName}`}>
          {item.courseName}
        </Link>
      </span>
      {item.gradingWeight !== undefined && (
        <span>
          {dotSeparator}
          <span css={styles.gradeWeight} data-e2e="recent-grades-item-grade-weight">
            {_t('#{gradingWeight}% Weight', { gradingWeight: truncToPlace(item.gradingWeight * 100, 2) })}
          </span>
        </span>
      )}
    </span>
  );

  const itemIcon = (
    <ItemIcon size={24} type={item.assignmentType as ItemType} useCdsIcon={true} cdsIconProps={{ size: 'large' }} />
  );

  return (
    <RecentGradesListItem
      itemIcon={itemIcon}
      itemTypeName={getItemTypePrefixTranslationByTypeName(
        item.assignmentType as ItemType,
        item.assignmentType === 'staffGraded' || item.assignmentType === 'ungradedAssignment'
          ? {
              isTeamAssignment: item.submissionType === AssignmentSubmissionType.SUBMISSION_TYPE_TEAM,
              isGraded: item.assignmentType === 'staffGraded',
            }
          : undefined
      )}
      itemName={item.assignmentName}
      itemUrl={item.assignmentUrl}
      statusPill={statusPill}
      subHeading={subHeading}
      trackingName="recent_grades_item_link"
    />
  );
};
